import React from 'react';
import { Popup } from 'antd-mobile';
import styled, { ThemeProvider, css, keyframes } from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import type { Scooter } from '../../services/api/types';
import {
  selectScooters,
  selectCurrentScooter,
  selectSelectedScooterVin,
  selectShowScooterSelector,
  selectScooterLoading,
  setSelectedScooter,
  toggleScooterSelector,
  closeScooterSelector,
} from '../../store/slices/scooterSlice';
import {
  chipLabel,
  useScooterReadiness,
  type ScooterChipState,
} from '../../hooks/useScooterReadiness';
import { r, riderLight } from '../../theme/riderTheme';
import {
  CloseButton,
  DragHandle,
  Sheet,
  SheetScroll,
} from '../../theme/sheetPrimitives';

// ─── Trigger pill in the app bar ───────────────────────────────────────────

const SelectorContainer = styled.button.attrs({ type: 'button' as const })`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px 6px 10px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.10);
  border-radius: 999px;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, transform 120ms ease;
  min-width: 0;
  max-width: min(46vw, 200px);
  min-height: 36px;
  color: #fff;
  font-family: 'Geist', sans-serif;

  &:hover {
    background: rgba(255, 255, 255, 0.10);
    border-color: rgba(255, 255, 255, 0.18);
  }
  &:active {
    transform: scale(0.98);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(192, 223, 22, 0.30);
  }
`;

const ScooterInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const ScooterName = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ScooterModel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColorDot = styled.span<{ $color: string }>`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 0 0 1px rgba(18, 18, 21, 0.6);
`;

const TriggerChevron = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 10px;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 200ms ease;
`;

const ringSpin = keyframes`
  to { transform: rotate(360deg); }
`;

// Loading spinner used both in the trigger pill (white-on-midnight) and in
// the sheet body (lime-on-light).
const TriggerSpinner = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.18);
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 720ms linear infinite;
`;

// ─── Bottom-sheet contents ─────────────────────────────────────────────────

const Eyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin: 4px 0 8px;
`;

const SheetTitle = styled.h2`
  margin: 0 0 16px;
  font-family: 'Geist', sans-serif;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: ${({ theme }) => r(theme).text.primary};
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 8px;
`;

const Option = styled.button.attrs({ type: 'button' as const })<{
  $selected: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  background: ${({ theme, $selected }) =>
    $selected ? r(theme).surface.raisedSelected : r(theme).surface.sheet};
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? r(theme).accent.limeDeep : r(theme).surface.edge};
  border-radius: 14px;
  cursor: pointer;
  text-align: left;
  font-family: 'Geist', sans-serif;
  color: ${({ theme }) => r(theme).text.primary};
  transition: background 160ms ease, border-color 160ms ease, transform 120ms ease;

  &:hover {
    background: ${({ theme, $selected }) =>
      $selected ? r(theme).surface.raisedSelected : 'rgba(18, 18, 21, 0.03)'};
    border-color: ${({ theme, $selected }) =>
      $selected ? r(theme).accent.limeDeep : r(theme).surface.edgeStrong};
  }
  &:active {
    transform: scale(0.995);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ theme }) => r(theme).glow.selectionLime};
  }

  ${({ $selected, theme }) =>
    $selected &&
    css`
      &::before {
        content: '';
        position: absolute;
        left: -1px;
        top: 14px;
        bottom: 14px;
        width: 3px;
        border-radius: 0 2px 2px 0;
        background: ${r(theme).accent.lime};
        box-shadow: 0 0 8px ${r(theme).accent.lime};
      }
    `}
`;

const OptionColor = styled(ColorDot)`
  width: 14px;
  height: 14px;
  border: 1px solid ${({ theme }) => r(theme).surface.edgeStrong};
  box-shadow: 0 0 0 1px ${({ theme }) => r(theme).surface.sheet};
`;

const OptionText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionPlate = styled.div`
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  color: ${({ theme }) => r(theme).text.primary};
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  overflow: hidden;
`;

const OptionModel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => r(theme).text.muted};
`;

const OptionVin = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.06em;
  color: ${({ theme }) => r(theme).text.faint};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OptionMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
`;

const Chip = styled.span<{ $state: ScooterChipState }>`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  padding: 4px 9px;
  border-radius: 999px;
  white-space: nowrap;

  ${({ $state, theme }) => {
    const t = r(theme);
    switch ($state) {
      case 'restricted':
      case 'needs_packs':
        return css`
          background: rgba(255, 25, 35, 0.05);
          border: 1px solid rgba(255, 25, 35, 0.32);
          color: ${t.accent.alert};
        `;
      case 'ready':
        return css`
          background: ${t.surface.raisedSelected};
          border: 1px solid ${t.accent.limeDeep};
          color: ${t.accent.ok};
        `;
      case 'unavailable':
      case 'checking':
      default:
        return css`
          background: ${t.surface.inputIdle};
          border: 1px solid ${t.surface.edge};
          color: ${t.text.faint};
        `;
    }
  }}
`;

const SelectedTick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ theme }) => r(theme).accent.lime};
  color: ${({ theme }) => r(theme).cta.bg};
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 0 0 1px rgba(192, 223, 22, 0.45);
`;

const SheetSpinnerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 16px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
`;

const SheetSpinner = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => r(theme).surface.edgeStrong};
  border-top-color: ${({ theme }) => r(theme).accent.lime};
  animation: ${ringSpin} 720ms linear infinite;
`;

const EmptyState = styled.div`
  padding: 32px 16px 40px;
  text-align: center;
`;

const EmptyEyebrow = styled.div`
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${({ theme }) => r(theme).text.faint};
  margin-bottom: 10px;
`;

const EmptyTitle = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${({ theme }) => r(theme).text.primary};
  margin-bottom: 6px;
`;

const EmptyBody = styled.div`
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => r(theme).text.muted};
`;

const getScooterPrimaryLabel = (scooter: Scooter): string => {
  const plate = scooter.plate_number?.trim();
  if (plate) return plate;
  return scooter.vin.length >= 4 ? scooter.vin.slice(-4) : scooter.vin;
};

const ScooterSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const scooters = useAppSelector(selectScooters);
  const currentScooter = useAppSelector(selectCurrentScooter);
  const selectedScooterVin = useAppSelector(selectSelectedScooterVin);
  const showSelector = useAppSelector(selectShowScooterSelector);
  const loading = useAppSelector(selectScooterLoading);
  const readinessByVin = useScooterReadiness(scooters);

  const handleToggleSelector = () => {
    dispatch(toggleScooterSelector());
  };

  const handleCloseSelector = () => {
    dispatch(closeScooterSelector());
  };

  const handleScooterSelect = (vin: string) => {
    if (vin !== selectedScooterVin) {
      dispatch(setSelectedScooter(vin));
    }
    dispatch(closeScooterSelector());
  };

  const renderTrigger = () => {
    if ((loading.scooters || loading.switching) && !currentScooter) {
      return (
        <SelectorContainer onClick={handleToggleSelector} aria-label="Loading scooters">
          <TriggerSpinner aria-hidden />
          <ScooterInfo>
            <ScooterName>
              {loading.switching ? 'Switching…' : 'Loading…'}
            </ScooterName>
          </ScooterInfo>
          <TriggerChevron $isOpen={showSelector} aria-hidden>
            ▾
          </TriggerChevron>
        </SelectorContainer>
      );
    }

    if (loading.switching && currentScooter) {
      return (
        <SelectorContainer aria-label="Switching scooter">
          <TriggerSpinner aria-hidden />
          <ScooterInfo>
            <ScooterName>Switching…</ScooterName>
          </ScooterInfo>
        </SelectorContainer>
      );
    }

    if (!currentScooter) {
      return (
        <SelectorContainer
          onClick={handleToggleSelector}
          aria-label="No scooter assigned"
        >
          <ScooterInfo>
            <ScooterName>No scooter</ScooterName>
          </ScooterInfo>
          <TriggerChevron $isOpen={showSelector} aria-hidden>
            ▾
          </TriggerChevron>
        </SelectorContainer>
      );
    }

    return (
      <SelectorContainer
        onClick={handleToggleSelector}
        aria-label={`Switch scooter, currently ${getScooterPrimaryLabel(currentScooter)}`}
        aria-haspopup="dialog"
        aria-expanded={showSelector}
      >
        <ColorDot
          $color={currentScooter.color_code || '#999999'}
          aria-hidden
        />
        <ScooterInfo>
          <ScooterName>{getScooterPrimaryLabel(currentScooter)}</ScooterName>
          {currentScooter.model ? (
            <ScooterModel>{currentScooter.model}</ScooterModel>
          ) : null}
        </ScooterInfo>
        <TriggerChevron $isOpen={showSelector} aria-hidden>
          ▾
        </TriggerChevron>
      </SelectorContainer>
    );
  };

  return (
    <>
      {renderTrigger()}

      <Popup
        visible={showSelector}
        onMaskClick={handleCloseSelector}
        position="bottom"
        bodyStyle={{ background: 'transparent', padding: 0, boxShadow: 'none' }}
        destroyOnClose
      >
        <ThemeProvider theme={riderLight as never}>
          <Sheet role="dialog" aria-label="Select scooter">
            <DragHandle />
            <CloseButton onClick={handleCloseSelector} aria-label="Close scooter selector">
              ×
            </CloseButton>
            <Eyebrow>Scooter · Switch</Eyebrow>
            <SheetTitle>Select a scooter</SheetTitle>

            <SheetScroll>
              {loading.scooters && scooters.length === 0 ? (
                <SheetSpinnerRow>
                  <SheetSpinner aria-hidden />
                  <span>Loading scooters…</span>
                </SheetSpinnerRow>
              ) : scooters.length === 0 ? (
                <EmptyState>
                  <EmptyEyebrow>Scooters · None</EmptyEyebrow>
                  <EmptyTitle>No scooters assigned</EmptyTitle>
                  <EmptyBody>
                    Contact support to add a scooter to your account.
                  </EmptyBody>
                </EmptyState>
              ) : (
                <OptionList>
                  {scooters.map(scooter => {
                    const selected = scooter.vin === selectedScooterVin;
                    const chipState: ScooterChipState =
                      readinessByVin[scooter.vin] ?? 'checking';
                    return (
                      <Option
                        key={scooter.vin}
                        $selected={selected}
                        onClick={() => handleScooterSelect(scooter.vin)}
                        aria-pressed={selected}
                      >
                        <OptionColor
                          $color={scooter.color_code || '#999999'}
                          aria-hidden
                        />
                        <OptionText>
                          <OptionPlate>
                            {getScooterPrimaryLabel(scooter)}
                            {scooter.model ? (
                              <OptionModel>{scooter.model}</OptionModel>
                            ) : null}
                          </OptionPlate>
                          <OptionVin>VIN · {scooter.vin}</OptionVin>
                        </OptionText>
                        <OptionMeta>
                          <Chip $state={chipState}>{chipLabel(chipState)}</Chip>
                          {selected ? (
                            <SelectedTick aria-hidden>✓</SelectedTick>
                          ) : null}
                        </OptionMeta>
                      </Option>
                    );
                  })}
                </OptionList>
              )}
            </SheetScroll>
          </Sheet>
        </ThemeProvider>
      </Popup>
    </>
  );
};

export default ScooterSelector;
